import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Cloud as GoogleIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { sprekersApi } from '../api';
import { getGoogleConfig } from '../google/googleConfig';
import { describeGoogleError, pickPresentation, requestDriveFileToken } from '../google/googleSlides';
import { loadGoogleIdentityServices, loadGooglePicker } from '../google/googleScripts';
import type { PresentationTemplate } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (template: PresentationTemplate) => void;
}

/**
 * Nieuw sjabloon -> Importeren vanuit Google Slides. Zelfde Picker, scope en tokenregels als de
 * presentatie-import: het access-token bestaat alleen binnen importFromGoogle en gaat nooit in state of storage.
 */
export default function GoogleSlidesTemplateImportDialog({ open, onOpenChange, onImported }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'editable' | 'visual'>('editable');
  // De Google Picker staat buiten deze modale dialoog; zolang de dialoog open is blokkeert Radix alle
  // klikken erbuiten. Daarom verdwijnt de dialoog (state blijft bewaard) zolang de kiezer open is.
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleConfig = getGoogleConfig();

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setError(null);
    // Alvast laden: de Google-popup moet direct vanuit de klik kunnen openen.
    if (googleConfig) {
      loadGoogleIdentityServices().catch(() => undefined);
      loadGooglePicker().catch(() => undefined);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const importFromGoogle = async () => {
    if (!googleConfig) return;
    setBusy(true);
    setError(null);
    try {
      const accessToken = await requestDriveFileToken(googleConfig.clientId);
      setPicking(true);
      let picked;
      try {
        picked = await pickPresentation(accessToken, googleConfig);
      } finally {
        setPicking(false);
      }
      if (!picked) return; // geannuleerd
      const template = await sprekersApi.importGoogleSlidesTemplate({
        file_id: picked.id, access_token: accessToken,
        name: name.trim() || undefined, description: description.trim() || undefined, mode,
      });
      toast.success('Sjabloon opgeslagen');
      onImported(template);
      onOpenChange(false);
    } catch (err: any) {
      const message = describeGoogleError(err, 'Importeren uit Google Slides mislukt. Probeer het opnieuw.');
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open && !picking} onOpenChange={(next: boolean) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Importeren vanuit Google Slides</DialogTitle>
          <DialogDescription>
            Je kiest een presentatie in Google Slides; Zoea slaat die op als herbruikbaar sjabloon voor je organisatie.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div role="radiogroup" aria-label="Opslaan als" className="space-y-2 rounded-md bg-slate-50 p-3">
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input type="radio" name="template-mode" checked={mode === 'editable'} disabled={busy} onChange={() => setMode('editable')} className="mt-1" />
              <span>
                <span className="font-medium text-slate-900">Bewerkbaar sjabloon</span>
                <span className="block text-xs text-slate-600">
                  Tekst, afbeeldingen, vormen en achtergronden worden losse onderdelen die je na gebruik in Zoea Studio aanpast.
                  Wat niet kan worden overgenomen (bijvoorbeeld tabellen) zie je na het importeren.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input type="radio" name="template-mode" checked={mode === 'visual'} disabled={busy} onChange={() => setMode('visual')} className="mt-1" />
              <span>
                <span className="font-medium text-slate-900">Visueel sjabloon</span>
                <span className="block text-xs text-slate-600">
                  Elke pagina wordt een afbeelding en de opmaak blijft behouden. Tekstvakken en vormen zijn niet los te bewerken.
                </span>
              </span>
            </label>
            <p className="text-xs text-slate-600">
              Animaties, video en spreeknotities worden niet meegenomen. Het bestand in Google wordt alleen gelezen en blijft ongewijzigd.
            </p>
          </div>
          <div>
            <Label htmlFor="google-template-name">Naam</Label>
            <Input
              id="google-template-name" value={name} disabled={busy} maxLength={200}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              placeholder="Standaard de naam in Google"
            />
          </div>
          <div>
            <Label htmlFor="google-template-description">Beschrijving (optioneel)</Label>
            <Textarea
              id="google-template-description" value={description} disabled={busy} maxLength={1000} rows={2}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            />
          </div>
          {busy && <p className="text-sm text-slate-500">Even geduld; rond de keuze in Google af en wacht op de import.</p>}
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button type="button" onClick={importFromGoogle} disabled={busy || !googleConfig}>
            <GoogleIcon className="mr-2 h-4 w-4" />
            {busy ? 'Bezig met importeren...' : 'Kies presentatie in Google'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
